import {
  configureQuery,
  EditStatusEnum,
  Events,
  scrollToElement,
  useEditorStore,
  useFlowBuilder2GetOrCreateDraftFlowMutation,
  useNotifications,
  useParams,
} from '@diana/core';
import { useQuery } from '@tanstack/react-query';
import { Fragment, PropsWithChildren, useCallback, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlowBuilder2PageStore } from '../Store/useFlowBuilder2PageStore';
import { useActiveView } from '../Support/useActiveView';
import { useClosePage } from '../Support/useClosePage';
import { FLOW_BUILDER2_PAGE_NS } from '../i18n';
import { FlowBuilder2PageContext } from './FlowBuilder2PageContext';

export const FlowBuilder2PageContextProvider = (props: PropsWithChildren) => {
  const { children } = props;
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const { flowId } = useParams<{ flowId: string }>();
  const notification = useNotifications();

  const editorFlowId = useEditorStore((s) => s.flow?.id ?? '');
  const setFlow = useEditorStore((s) => s.setFlow);

  const setAiPendingStatuses = useFlowBuilder2PageStore((s) => s.setAiPendingStatuses);

  const close = useClosePage();

  const [activeView, setActiveView] = useActiveView();

  const {
    mutate,
    error,
    isPending: isLoadingFlow,
  } = useFlowBuilder2GetOrCreateDraftFlowMutation({
    onSuccess: (draftFlow) => {
      setFlow(draftFlow);
    },
  });

  const flowAndLayoutEditStatusQuery = configureQuery.getFlowAndLayoutEditStatus({
    flowId: editorFlowId,
  });

  const {
    data: editStatusData,
    isLoading: isLoadingEditStatus,
    isError,
  } = useQuery({
    enabled: !!editorFlowId,
    gcTime: 0,
    queryKey: flowAndLayoutEditStatusQuery.key,
    queryFn: flowAndLayoutEditStatusQuery.fn,
  });

  useEffect(() => {
    if (isError) {
      notification.error({ message: t('errors.editStatusFetchFailed') });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  useLayoutEffect(() => {
    if (editStatusData) {
      const { flowStatus, layoutStatus } = editStatusData;
      const result: { flows: string[]; layouts: string[] } = { flows: [], layouts: [] };

      if (flowStatus.editStatus === EditStatusEnum.Started) {
        result.flows.push(flowStatus.resourceId);
      }

      result.layouts = layoutStatus
        .filter((item) => item.editStatus === EditStatusEnum.Started)
        .map((item) => item.resourceId);

      setAiPendingStatuses(result);
      if (result.layouts.length > 0 && result.flows.length === 0) {
        const firstPendingLayoutId = result.layouts[0];
        Events.SetFlowBuilderActiveTask.post({ layoutId: firstPendingLayoutId });
        setTimeout(
          () => scrollToElement({ id: firstPendingLayoutId, isBoundingClientRect: true }),
          500,
        );
      }
    }
  }, [editStatusData, setAiPendingStatuses]);

  const isLoading = isLoadingFlow || isLoadingEditStatus;

  const reload = useCallback(() => {
    mutate({ flowId: flowId! });
  }, [mutate, flowId]);

  useLayoutEffect(() => {
    if (flowId && !editorFlowId) {
      reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowId]);

  return (
    <FlowBuilder2PageContext.Provider
      key={flowId}
      value={{ close, reload, isLoading, error, activeView, setActiveView }}
    >
      <Fragment>{children}</Fragment>
    </FlowBuilder2PageContext.Provider>
  );
};
